import React, { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useOutletContext } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { TradingAccountsOutletCtx } from '@pages/Traders-Room/Trading-Accounts/TradingAccounts.page'
import { TradeButtonLinkWrapper } from '@pages/Traders-Room/Trading-Accounts/components/TradeButtonLinkWrapper'
import { TradingAccountActionsDropdown } from '@pages/Traders-Room/Trading-Accounts/components/TradingAccountActionsDropdown'

import { useModal } from '@context/ModalsContext'

import { SensitiveData } from '@ui/Privacy/SensitiveData'
import { Body1, Body2, Caption1 } from '@ui/Typography'
import { ActionIcon } from '@ui/components/ActionIcon'
import { Badge } from '@ui/components/Badge/Badge'
import { Button, IconButton } from '@ui/components/Button'
import { Link, LinkProps } from '@ui/components/Link'
import { Tooltip } from '@ui/components/Tooltip/Tooltip'
import { ArrowCircleIcon } from '@ui/icons/ArrowCircle.icon'
import { CopyIcon } from '@ui/icons/Copy.icon'
import { InfoIcon } from '@ui/icons/Info.icon'
import { SettingIcon } from '@ui/icons/Setting.icon'
import { MT4Icon } from '@ui/icons/platforms/MT4.icon'
import { MT5Icon } from '@ui/icons/platforms/MT5.icon'
import { MobileAppIcon } from '@ui/icons/platforms/MobileApp.icon'
import { TradingViewIcon } from '@ui/icons/platforms/TradingView.icon'

import { PageQuery } from '@utils/ApiClient'
import { CommonPaths } from '@utils/path-common'
import useSensitiveDataFlag from '@utils/useSensitiveDataFlag'

import { PlatformType, isPlatformTypeDisabled } from '@model/PlatformTypeDto'
import {
  PlatformTypeEnum,
  getDisplayPlatformName,
  isMT4PlatformType,
  isMT5PlatformType,
  isTMTPlatformType,
  isTradingViewPlatformType,
} from '@model/PlatformTypeEnum'
import {
  DemoTradingAccount,
  isTradingAccountStatusApproved,
  isTradingAccountStatusCloseOnly,
} from '@model/TradingAccount'

import { useCopy } from '@hooks/useCopy'
import { useFormatNumber } from '@hooks/useFormatNumber'

import { Box } from '@shared/components/Box'
import { CopyLink } from '@shared/components/CopyLink'
import { InformationModal } from '@shared/modals/Information.modal'

import { CreateTradingAccountCTA } from '../components/CreateTradingAccountCTA'

import styles from './DemoTradingAccountsCards.module.scss'

export interface DemoTradingAccountsCardsProps {
  tableQuery: { data: DemoTradingAccount[]; isLoading: boolean }
  pageQuery?: PageQuery
  setPageQuery?: (q: PageQuery) => void
  platformTypes?: PlatformType[]
  onLeverageChange?: (args: { tradingAccount: DemoTradingAccount }) => void
  onDisplayDynamicLeverageInfo?: () => void
}

const getPlatformLogo = (platformTypeId: PlatformTypeEnum) => {
  switch (platformTypeId) {
    case PlatformTypeEnum.MT4:
      return <MT4Icon size={40} />
    case PlatformTypeEnum.MT5:
      return <MT5Icon size={40} />
    case PlatformTypeEnum.TickmillTrader:
      return <MobileAppIcon size={40} />
    default:
      return <TradingViewIcon size={40} />
  }
}

const isDemoWebTraderAvailable = (ta: DemoTradingAccount) =>
  isTradingAccountStatusApproved(ta.status) || isTradingAccountStatusCloseOnly(ta.status)

interface AccountHeaderProps {
  tradingAccount: DemoTradingAccount
  balance: string
  isTradeDisabled: boolean
  isPlatformDisabled: boolean
}

const AccountHeader: FC<AccountHeaderProps> = ({
  tradingAccount,
  balance,
  isTradeDisabled,
  isPlatformDisabled,
}) => {
  const { t } = useTranslation()

  return (
    <Box className={styles.balanceWrapper}>
      <Box className={styles.logoCircle}>{getPlatformLogo(tradingAccount.platformType.id)}</Box>
      <Box className={styles.balance}>
        <Caption1 className={styles.accountName} isParagraph>
          {tradingAccount.nickname || tradingAccount.name}
        </Caption1>
        <Body1 className={styles.accountBalance} semibold>
          <SensitiveData>{balance}</SensitiveData>
        </Body1>
      </Box>
      {(isPlatformDisabled || isTradeDisabled) &&
        !isTradingAccountStatusCloseOnly(tradingAccount.status) && (
          <Badge type='yellow' size='xxs'>
            {t('Deactivated')}
          </Badge>
        )}
      {isTradingAccountStatusCloseOnly(tradingAccount.status) && (
        <Badge type='yellow' size='xxs' contrast='low'>
          {t('Trading Account.Close Only')}
        </Badge>
      )}
    </Box>
  )
}

interface AccountActionsProps {
  tradingAccount: DemoTradingAccount
  isTradeDisabled: boolean
  isPlatformDisabled: boolean
  isMT4: boolean
  isMT5: boolean
  handleWebTraderModalVisibility: (tradingAccount: DemoTradingAccount) => void
  navigate: ReturnType<typeof useNavigate>
  handleSetSelectedTradingAccount: (tradingAccount: DemoTradingAccount) => void
  openResetPasswordModal: () => void
  handleChangePasswordRestricted: () => void
  handleResetPasswordRestricted: () => void
  handleChangeInvestorPasswordRestricted: () => void
  openPassInfoModal: (title: string) => void
}

const AccountActions: FC<AccountActionsProps> = ({
  tradingAccount,
  isTradeDisabled,
  isPlatformDisabled,
  isMT4,
  isMT5,
  handleWebTraderModalVisibility,
  handleChangePasswordRestricted,
  handleChangeInvestorPasswordRestricted,
  openPassInfoModal,
  handleResetPasswordRestricted,
  handleSetSelectedTradingAccount,
  openResetPasswordModal,
}) => {
  const { t } = useTranslation()
  const handleResetPassword = () => {
    handleSetSelectedTradingAccount(tradingAccount)
    openResetPasswordModal()
  }
  return (
    <Box className={styles.actions} gap='x8'>
      <Box flex gap='x8' align='center'>
        <TradeButtonLinkWrapper
          disabled={isTradeDisabled || isPlatformDisabled}
          tradingAccount={tradingAccount}
        >
          <Button.Primary size='sm'>{t('Trade')}</Button.Primary>
        </TradeButtonLinkWrapper>
        {isPlatformDisabled && (
          <ActionIcon
            icon={InfoIcon}
            size='x16'
            onClick={() => handleWebTraderModalVisibility(tradingAccount)}
          />
        )}
      </Box>
      <Tooltip
        disabled={isTradingAccountStatusApproved(tradingAccount.status)}
        content={t('This action is not available')}
      >
        <Link
          to={
            isTradingAccountStatusApproved(tradingAccount.status)
              ? `/dashboard/traders-room/trading-accounts/${tradingAccount.id}/demo/add-balance`
              : '#'
          }
          onClick={(e) => {
            if (!isTradingAccountStatusApproved(tradingAccount.status)) e.preventDefault()
          }}
        >
          <Button.Secondary
            size='sm'
            disabled={!isTradingAccountStatusApproved(tradingAccount.status)}
          >
            {`${t('Trading Account.Add')} ${t('Trading Account.Balance')}`}
          </Button.Secondary>
        </Link>
      </Tooltip>
      {isMT4 || isMT5 ? (
        <IconButton.Secondary
          size='md'
          icon={SettingIcon}
          onClick={(e) => {
            e.stopPropagation()
            openPassInfoModal(`${tradingAccount.name}`)
          }}
        />
      ) : (
        <TradingAccountActionsDropdown
          tradingAccount={tradingAccount}
          restrictions={[]}
          onChangePasswordRestrictedClick={handleChangePasswordRestricted}
          onChangeInvestorPasswordRestrictedClick={handleChangeInvestorPasswordRestricted}
          onResetPasswordClick={handleResetPassword}
          onResetPasswordRestrictedClick={handleResetPasswordRestricted}
        />
      )}
    </Box>
  )
}

interface AccountDetailsProps {
  tradingAccount: DemoTradingAccount
  balance: string
  equity: string
  isMT4: boolean
  isMT5: boolean
  isDynamicLeverage: boolean
  sensitiveDataFlag: boolean
  onLeverageChange?: (args: { tradingAccount: DemoTradingAccount }) => void
  onDisplayDynamicLeverageInfo?: () => void
  navigate: ReturnType<typeof useNavigate>
}

const AccountDetails: FC<AccountDetailsProps> = ({
  tradingAccount,
  equity,
  isMT4,
  isMT5,
  isDynamicLeverage,
  sensitiveDataFlag,
  onLeverageChange,
  onDisplayDynamicLeverageInfo,
  navigate,
}) => {
  const { t } = useTranslation()
  const { copy } = useCopy()

  return (
    <Box className={styles.accountDetails}>
      <Box className={styles.detailItem}>
        <Caption1 isParagraph>{t('Trading Account.Account Type')}</Caption1>
        <Body2 isParagraph>{tradingAccount.accountType?.name}</Body2>
      </Box>
      <Box className={styles.detailItem}>
        <Caption1 isParagraph>{t('Trading Account.Leverage')}</Caption1>
        <Box flex gap='x8' align='center'>
          {isDynamicLeverage ? (
            <Body2>
              <LeverageLink
                tradingAccount={tradingAccount}
                onClick={(e) => {
                  e.stopPropagation()
                  onLeverageChange?.({ tradingAccount })
                }}
              />
            </Body2>
          ) : (
            <Body2>
              <LeverageLink
                tradingAccount={tradingAccount}
                state={{ tradingAccountId: tradingAccount.id }}
                onClick={(e) => {
                  e.stopPropagation()
                  onLeverageChange?.({ tradingAccount })
                }}
              />
            </Body2>
          )}
          {isDynamicLeverage && tradingAccount.platformType.dynamicLeverage && (
            <ActionIcon
              size='x16'
              icon={InfoIcon}
              onClick={(e) => {
                e.stopPropagation()
                onDisplayDynamicLeverageInfo?.()
              }}
            />
          )}
        </Box>
      </Box>
      <Box className={styles.detailItem}>
        <Caption1 isParagraph>{t('Trading Account.Equity')}</Caption1>
        <Body2 isParagraph>
          <SensitiveData>{equity}</SensitiveData>
        </Body2>
      </Box>
      <Box className={styles.detailItem}>
        <Caption1 isParagraph>{t('Trading Account.Platform')}</Caption1>
        <Box flex gap='x4' align='center'>
          <Body2>{getDisplayPlatformName(tradingAccount.platformType)}</Body2>

          {isTMTPlatformType(tradingAccount.platformType.id) && (
            <Tooltip
              direction='bottom'
              content={
                <Body2>
                  {t(
                    'Trading Account.You can use your Tickmill Trader login credentials to access the TradingView platform'
                  )}
                </Body2>
              }
              primaryButton={{
                label: t('Trading Account.Download'),
                onClick: () =>
                  navigate('/dashboard/traders-room/platforms/tickmilltrader/webtrader'),
                size: 'sm',
              }}
            >
              <IconButton.Ghost icon={ArrowCircleIcon.Down} />
            </Tooltip>
          )}
          {isTradingViewPlatformType(tradingAccount.platformType.id) && (
            <Tooltip
              direction='bottom'
              content={
                <Body2>
                  {t(
                    'Trading Account.You can use your TradingView login credentials to access the Tickmill Trader Mobile App'
                  )}
                </Body2>
              }
              primaryButton={{
                label: t('Trading Account.Download'),
                onClick: () => navigate('/dashboard/traders-room/platforms/tradingview'),
                size: 'sm',
              }}
            >
              <IconButton.Ghost icon={ArrowCircleIcon.Down} />
            </Tooltip>
          )}
          {isMT4 && (
            <IconButton.Ghost
              icon={ArrowCircleIcon.Down}
              onClick={() => navigate('/dashboard/traders-room/platforms/mt4')}
            />
          )}
          {isMT5 && (
            <IconButton.Ghost
              icon={ArrowCircleIcon.Down}
              onClick={() => navigate('/dashboard/traders-room/platforms/mt5')}
            />
          )}
        </Box>
      </Box>

      {!(isMT4 || isMT5) && (
        <Box className={styles.detailItem}>
          <Caption1 isParagraph wrap='nowrap'>
            {t('Trading Account.Account Number')}
          </Caption1>
          <Box flex gap='x4' align='center'>
            <Body2 isParagraph>{tradingAccount.name}</Body2>
            <CopyLink value={tradingAccount.name}>
              <ActionIcon icon={CopyIcon} size='x16' />
            </CopyLink>
          </Box>
        </Box>
      )}

      {tradingAccount.externalId && (
        <Box className={styles.detailItem}>
          <Caption1 isParagraph>{t('Trading Account.Username')}</Caption1>
          <Box flex gap='x4' align='center'>
            <Body2 isParagraph>
              {sensitiveDataFlag ? (
                <SensitiveData>{tradingAccount.externalId}</SensitiveData>
              ) : (
                tradingAccount.externalId
              )}
            </Body2>
            <ActionIcon
              icon={CopyIcon}
              size='x16'
              disabled={sensitiveDataFlag}
              onClick={() => copy(tradingAccount.externalId)}
            />
          </Box>
        </Box>
      )}
      {tradingAccount.introducingBroker?.name && (
        <Box className={styles.detailItem}>
          <Caption1 isParagraph>{t('Trading Account.Referral Code')}</Caption1>
          <Box flex gap='x4' align='center'>
            <Body2 isParagraph>{tradingAccount.introducingBroker?.name}</Body2>
            <ActionIcon
              icon={CopyIcon}
              size='x16'
              onClick={() => copy(tradingAccount.introducingBroker?.name)}
            />
          </Box>
        </Box>
      )}

      {(isMT4 || isMT5) && (
        <Box className={styles.detailItem}>
          <Caption1 isParagraph>{t('Trading Account.MT Login')}</Caption1>
          <Box flex gap='x4' align='center'>
            <Body2 isParagraph>{tradingAccount.platformOverview.login}</Body2>
            <ActionIcon
              icon={CopyIcon}
              size='x16'
              onClick={() => copy(tradingAccount.platformOverview.login)}
            />
          </Box>
        </Box>
      )}

      {(isMT4 || isMT5) && (
        <Box className={styles.detailItem}>
          <Caption1 isParagraph>{t('Trading Account.Server')}</Caption1>
          <Box flex gap='x4' align='center'>
            <Body2 isParagraph>{tradingAccount.server.publicName}</Body2>
            <ActionIcon
              icon={CopyIcon}
              size='x16'
              onClick={() => copy(tradingAccount.server.publicName)}
            />
          </Box>
        </Box>
      )}
    </Box>
  )
}

type LeverageLinkProps = LinkProps & {
  tradingAccount: DemoTradingAccount
  onClick?: (event: React.MouseEvent<HTMLElement>) => void
}

export const LeverageLink: FC<LeverageLinkProps> = (props) => {
  const { tradingAccount, onClick, ...restProps } = props

  const name = tradingAccount.leverage?.name

  const { isLeverageReadOnly } = useMemo(() => {
    const isMT4 = isMT4PlatformType(tradingAccount.platformType.id)
    const isMT5 = isMT5PlatformType(tradingAccount.platformType.id)
    const isLeverageReadOnly = isMT4 || isMT5

    return { isLeverageReadOnly }
  }, [tradingAccount])

  if (isLeverageReadOnly) {
    return name
  }

  return (
    <Link
      {...restProps}
      onClick={(e) => {
        e.stopPropagation()
        onClick?.(e)
      }}
    >
      {name}
    </Link>
  )
}

interface DemoTradingAccountCardProps {
  tradingAccount: DemoTradingAccount
  index: number
  platformTypes?: PlatformType[]
  formatMoney: (amount: number, currency: string) => string
  sensitiveDataFlag: boolean
  handleWebTraderModalVisibility: (tradingAccount: DemoTradingAccount) => void
  navigate: ReturnType<typeof useNavigate>
  handleSetSelectedTradingAccount: (tradingAccount: DemoTradingAccount) => void
  openResetPasswordModal: () => void
  handleChangePasswordRestricted: (tradingAccount: DemoTradingAccount) => void
  handleResetPasswordRestricted: (tradingAccount: DemoTradingAccount) => void
  handleChangeInvestorPasswordRestricted: (tradingAccount: DemoTradingAccount) => void
  openPassInfoModal: (title: string) => void
  onLeverageChange?: (args: { tradingAccount: DemoTradingAccount }) => void
  onDisplayDynamicLeverageInfo?: () => void
}

const DemoTradingAccountCard: FC<DemoTradingAccountCardProps> = ({
  tradingAccount,
  platformTypes,
  formatMoney,
  sensitiveDataFlag,
  handleWebTraderModalVisibility,
  navigate,
  handleSetSelectedTradingAccount,
  openResetPasswordModal,
  handleChangePasswordRestricted,
  handleResetPasswordRestricted,
  handleChangeInvestorPasswordRestricted,
  openPassInfoModal,
  onLeverageChange,
  onDisplayDynamicLeverageInfo,
}) => {
  const computedData = useMemo(() => {
    const isTradeDisabled = !isDemoWebTraderAvailable(tradingAccount)
    const isMT4 = isMT4PlatformType(tradingAccount.platformType.id)
    const isMT5 = isMT5PlatformType(tradingAccount.platformType.id)
    const isPlatformDisabled =
      platformTypes && isPlatformTypeDisabled(platformTypes, tradingAccount.platformType.id)
    const balance = formatMoney(
      tradingAccount.platformOverview?.balance,
      tradingAccount.currency.id
    )
    const equity = formatMoney(tradingAccount.platformOverview.equity, tradingAccount.currency.id)
    const isDynamicLeverage = tradingAccount.leverage?.name === 'Dynamic'

    return {
      isTradeDisabled,
      isMT4,
      isMT5,
      isPlatformDisabled,
      balance,
      equity,
      isDynamicLeverage,
    }
  }, [tradingAccount, platformTypes, formatMoney])

  const handleChangePasswordRestrictedBound = () => handleChangePasswordRestricted(tradingAccount)
  const handleResetPasswordRestrictedBound = () => handleResetPasswordRestricted(tradingAccount)
  const handleChangeInvestorPasswordRestrictedBound = () =>
    handleChangeInvestorPasswordRestricted(tradingAccount)

  return (
    <Box className={styles.cardWrapper} key={tradingAccount.id}>
      <Box className={styles.card} gap='x16'>
        <Box className={styles.title}>
          <AccountHeader
            tradingAccount={tradingAccount}
            balance={computedData.balance}
            isPlatformDisabled={!!computedData.isPlatformDisabled}
            isTradeDisabled={computedData.isTradeDisabled}
          />
          <AccountActions
            tradingAccount={tradingAccount}
            isTradeDisabled={computedData.isTradeDisabled}
            isPlatformDisabled={!!computedData.isPlatformDisabled}
            isMT4={computedData.isMT4}
            isMT5={computedData.isMT5}
            handleWebTraderModalVisibility={handleWebTraderModalVisibility}
            navigate={navigate}
            handleSetSelectedTradingAccount={handleSetSelectedTradingAccount}
            openResetPasswordModal={openResetPasswordModal}
            handleChangePasswordRestricted={handleChangePasswordRestrictedBound}
            handleResetPasswordRestricted={handleResetPasswordRestrictedBound}
            handleChangeInvestorPasswordRestricted={handleChangeInvestorPasswordRestrictedBound}
            openPassInfoModal={openPassInfoModal}
          />
        </Box>
        <Box className={styles.divider}></Box>
        <AccountDetails
          tradingAccount={tradingAccount}
          balance={computedData.balance}
          equity={computedData.equity}
          isMT4={computedData.isMT4}
          isMT5={computedData.isMT5}
          isDynamicLeverage={computedData.isDynamicLeverage}
          sensitiveDataFlag={sensitiveDataFlag}
          onLeverageChange={onLeverageChange}
          onDisplayDynamicLeverageInfo={onDisplayDynamicLeverageInfo}
          navigate={navigate}
        />
      </Box>
    </Box>
  )
}

export const DemoTradingAccountsCards: React.FC<DemoTradingAccountsCardsProps> = (props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { formatMoney } = useFormatNumber()
  const sensitiveDataFlag = useSensitiveDataFlag()

  const {
    openWebTraderMT5IsUnavailableModal,
    openWebTraderMT4IsUnavailableModal,
    openWebTraderTickmillTraderIsUnavailableModal,
    handleSetSelectedTradingAccount,
    handleChangePasswordRestricted,
    handleResetPasswordRestricted,
    handleChangeInvestorPasswordRestricted,
    openResetPasswordModal,
  } = useOutletContext<TradingAccountsOutletCtx>()

  const passInfoModal = useModal(InformationModal, {
    description: t('Trading Account.The password for demo trading accounts'),
  })

  const {
    tableQuery: { data },
    platformTypes,
    onLeverageChange,
    onDisplayDynamicLeverageInfo,
  } = props

  const handleWebTraderModalVisibility = (tradingAccount: DemoTradingAccount) => {
    switch (tradingAccount.platformType.id) {
      case PlatformTypeEnum.MT4:
        return openWebTraderMT4IsUnavailableModal()
      case PlatformTypeEnum.MT5:
        return openWebTraderMT5IsUnavailableModal()
      default:
        return openWebTraderTickmillTraderIsUnavailableModal()
    }
  }

  const openPassInfoModal = (title: string) => {
    passInfoModal.open({ title })
  }

  return (
    <Box className={styles.cards}>
      {!data.length ? (
        <CreateTradingAccountCTA createTradingAccountPath={CommonPaths.NewDemoTradingAccount} />
      ) : (
        data.map((tradingAccount, index) => (
          <React.Fragment key={tradingAccount.id}>
            <DemoTradingAccountCard
              tradingAccount={tradingAccount}
              index={index}
              platformTypes={platformTypes}
              formatMoney={formatMoney}
              sensitiveDataFlag={sensitiveDataFlag}
              handleWebTraderModalVisibility={handleWebTraderModalVisibility}
              navigate={navigate}
              handleSetSelectedTradingAccount={handleSetSelectedTradingAccount}
              openResetPasswordModal={openResetPasswordModal}
              handleChangePasswordRestricted={handleChangePasswordRestricted}
              handleResetPasswordRestricted={handleResetPasswordRestricted}
              handleChangeInvestorPasswordRestricted={handleChangeInvestorPasswordRestricted}
              openPassInfoModal={openPassInfoModal}
              onLeverageChange={onLeverageChange}
              onDisplayDynamicLeverageInfo={onDisplayDynamicLeverageInfo}
            />
          </React.Fragment>
        ))
      )}
    </Box>
  )
}
